import { useState, type FormEvent } from "react";
import { errorMessage, fieldErrors } from "../../shared/api/errors.ts";
import { DEVICE_NAME_MAX_LENGTH, deviceName, useRenameDevice, type Device } from "../../shared/devices/devices.ts";
import { BottomSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { Input } from "../../shared/ui/Input/Input.tsx";
import styles from "./RenameSheet.module.css";

type Errors = { name?: string; form?: string };

/** Шторка переименования E-va: отдельного макета нет, каркас — Sheet (Figma 682:6431) с полем «Название». */
export function RenameSheet({ device, open, onClose }: { device: Device; open: boolean; onClose: () => void }) {
  return (
    <BottomSheet open={open} onClose={onClose} title="Переименовать E-va">
      {/* Шторка монтирует содержимое при каждом открытии: поле снова начинается с текущего названия. */}
      <RenameForm device={device} onDone={onClose} />
    </BottomSheet>
  );
}

function RenameForm({ device, onDone }: { device: Device; onDone: () => void }) {
  const [name, setName] = useState(deviceName(device));
  const [errors, setErrors] = useState<Errors>({});
  const mutation = useRenameDevice(device.id);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setErrors({ name: "Введите название" });
      return;
    }
    mutation.mutate(trimmed, { onSuccess: onDone, onError: (error) => setErrors(errorsFrom(error)) });
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <Input
        label="Название"
        name="name"
        autoComplete="off"
        maxLength={DEVICE_NAME_MAX_LENGTH}
        value={name}
        onChange={(event) => {
          setName(event.target.value);
          setErrors({});
        }}
        error={errors.name}
      />
      <div className={styles.actions}>
        <Button type="submit" loading={mutation.isPending}>
          Сохранить
        </Button>
        {errors.form && (
          <p className={styles.formError} role="alert">
            {errors.form}
          </p>
        )}
        <Button variant="secondary" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </form>
  );
}

function errorsFrom(error: Error): Errors {
  const byField = fieldErrors(error, ["name"] as const);
  return byField.name ? byField : { form: errorMessage(error) };
}
